<template>
    <div class="FormExample">
        <ComponentPlayground
            :component="TextInput"
            :defaults="textDefaults"
            :event-bindings="{ input: 'value' }"
            :event-transformers="{ input: domEventTransformer }"
            title="TextInput"
            description="Standard text input with optional mask, error, required, and disabled states."
        />
        <ComponentPlayground
            :component="NumberInput"
            :defaults="numberDefaults"
            :event-bindings="{ input: 'value' }"
            :event-transformers="{ input: domEventTransformer }"
            title="NumberInput"
            description="Numeric input with optional min, max, and error states."
        />
        <ComponentPlayground
            :component="TextareaInput"
            :defaults="textareaDefaults"
            :event-bindings="{ input: 'value' }"
            :event-transformers="{ input: domEventTransformer }"
            title="TextareaInput"
            description="Multi-line text input with configurable rows and optional maxlength."
        />
        <ComponentPlayground
            :component="EnumInput"
            :defaults="enumDefaults"
            :event-bindings="{ input: 'value' }"
            title="EnumInput"
            description="Dropdown select with optional custom value entry and error states."
        />
        <ComponentPlayground
            :component="RadioInput"
            :defaults="radioDefaults"
            :event-bindings="{ change: 'modelValue' }"
            :event-transformers="{ change: domEventTransformer }"
            title="RadioInput"
            description="One radio in a group; set modelValue to match value to select it."
        />
        <ComponentPlayground
            :component="CheckboxInput"
            :defaults="checkboxDefaults"
            :event-bindings="{ change: 'modelValue' }"
            :event-transformers="{ change: domEventTransformer }"
            title="CheckboxInput"
            description="One checkbox in a group; set modelValue to match value to select it."
        />
        <ComponentPlayground
            :component="PhoneInput"
            :defaults="phoneDefaults"
            :event-bindings="{ change: 'value' }"
            title="PhoneInput"
            description="International phone input with country code selection and validation."
        />
        <ComponentPlayground
            :component="DateInput"
            :defaults="dateDefaults"
            :event-bindings="{ change: 'value' }"
            title="DateInput"
            description="Date picker with calendar popover and ISO string output."
        />
    </div>
</template>

<style lang="scss" scoped>
.FormExample {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 56rem;
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import CheckboxInput from '@/modules/dynamic-form/components/style-inputs/checkbox-input/CheckboxInput.vue';
    import DateInput from '@/modules/dynamic-form/components/style-inputs/date-input/DateInput.vue';
    import EnumInput from '@/modules/dynamic-form/components/style-inputs/enum-input/EnumInput.vue';
    import NumberInput from '@/modules/dynamic-form/components/style-inputs/number-input/NumberInput.vue';
    import PhoneInput from '@/modules/dynamic-form/components/style-inputs/phone-input/PhoneInput.vue';
    import RadioInput from '@/modules/dynamic-form/components/style-inputs/radio-input/RadioInput.vue';
    import TextInput from '@/modules/dynamic-form/components/style-inputs/text-input/TextInput.vue';
    import TextareaInput from '@/modules/dynamic-form/components/style-inputs/textarea-input/TextareaInput.vue';

    import ComponentPlayground from './ComponentPlayground.vue';

    export default defineComponent({
        name: 'FormExample',
        components: { ComponentPlayground },
        data() {
            return {
                TextInput,
                NumberInput,
                TextareaInput,
                EnumInput,
                RadioInput,
                CheckboxInput,
                PhoneInput,
                DateInput,
                domEventTransformer: (e: any) => e?.target?.value ?? e,
                textDefaults: {
                    label: 'Text input',
                    value: '',
                    placeholder: 'Enter text…',
                    required: false,
                    disabled: false,
                    error: '',
                    mask: '',
                },
                numberDefaults: {
                    label: 'Number input',
                    value: '',
                    placeholder: 'Enter a number…',
                    required: false,
                    disabled: false,
                    error: '',
                    min: null,
                    max: null,
                },
                textareaDefaults: {
                    label: 'Textarea',
                    value: '',
                    placeholder: 'Enter longer text…',
                    required: false,
                    disabled: false,
                    rows: 4,
                    maxlength: null,
                },
                enumDefaults: {
                    label: 'Dropdown',
                    value: null,
                    options: [
                        { label: 'Option 1', value: 'option-1' },
                        { label: 'Option 2', value: 'option-2' },
                        { label: 'Option 3', value: 'option-3' },
                    ],
                    required: false,
                    disabled: false,
                    allowCustomValues: false,
                    error: '',
                },
                radioDefaults: {
                    name: 'radio-group',
                    label: 'Option A',
                    value: 'a',
                    modelValue: '',
                    disabled: false,
                },
                checkboxDefaults: {
                    name: 'checkbox-group',
                    label: 'Option A',
                    value: 'a',
                    modelValue: '',
                    disabled: false,
                },
                phoneDefaults: {
                    label: 'Phone',
                    value: '',
                    required: false,
                    disabled: false,
                },
                dateDefaults: {
                    label: 'Date',
                    value: '',
                    required: false,
                    disabled: false,
                },
            };
        },
    });
</script>
